import { Box } from '@mui/material';
import { Container } from '@mui/system';
import BaseTypography from 'components/typography/BaseTypography';
import { FontsEnum } from 'constants/enums/FontsEnum.ts';
import React from 'react';
import { colorPalette } from 'ui-blocks/ColorPalette';
import { reusableStyles } from 'ui-blocks/ReusableStyles';
import { getRedirectUrlWithParams } from '../../../utils/utils';

export default function PrivacyPolicyPage() {
	return (
		<Container
			maxWidth="tabletMid"
			sx={{
				padding: '40px',
			}}
		>
			<Box sx={[reusableStyles.flexColWithGap(33)]}>
				<BaseTypography
					styles={{
						fontSize: '40px',
						fontWeight: 540,
						lineHeight: '53.6px',
						letterSpacing: '0.15px',
						fontFamily: FontsEnum.ZODIAK,
					}}
					color={colorPalette.light3}
				>
					Privacy Policy
				</BaseTypography>
				<Box sx={[reusableStyles.flexColWithGap(24)]}>
					<Box sx={[reusableStyles.flexColWithGap(8)]}>
						<BaseTypography variant="t2_bold" styles={{ color: 'colors.white' }}>
							Introduction
						</BaseTypography>
						<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
							This Privacy Policy describes how Walrus Tech Inc. (&quot;Company&quot;,
							&quot;we&quot;, &quot;us&quot;, or &quot;our&quot;) collects, uses, shares, and stores
							information related to your use of the{' '}
							<span style={{ textDecoration: 'underline' }}>
								<a
									href={getRedirectUrlWithParams('https://app.affogato.ai/')}
									target="_blank"
									rel="noopener noreferrer"
									style={{ textDecoration: 'inherit', color: 'inherit' }}
								>
									Affogato.ai
								</a>
							</span>{' '}
							platform, including any related websites, services, or applications (collectively, the
							&quot;Platform&quot;).
							<br /> By using the Platform, you agree to the collection and use of information in
							accordance with this Privacy Policy. If you do not agree, please do not use the
							Platform.
						</BaseTypography>
					</Box>

					<Box sx={[reusableStyles.flexColWithGap(8)]}>
						<BaseTypography variant="t2_bold" styles={{ color: 'colors.white' }}>
							Information We Collect
						</BaseTypography>
						<Box
							component="ol"
							sx={[
								reusableStyles.flexColWithGap(12),
								{
									color: 'colors.white',
									margin: 0,
									alignItems: 'flex-start',
									fontSize: '12px',
									fontWeight: 600,
									padding: '0 20px',
								},
							]}
						>
							<Box component="li">
								<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
									<strong>Account Information:</strong> When you register an account, we collect
									your email address, username, and any other information you voluntarily provide.
								</BaseTypography>
							</Box>
							<Box component="li">
								<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
									<strong>Usage Information:</strong> We may collect information about how you
									access and use the Platform, such as your browser type, operating system, pages
									visited, and the time spent on those pages.
								</BaseTypography>
							</Box>
							<Box component="li">
								<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
									<strong>User-Generated Content:</strong> We store the content (e.g., text prompts,
									images, outputs) you generate or upload to the Platform to provide services to
									you.
								</BaseTypography>
								<Box
									component="ul"
									sx={[
										reusableStyles.flexCol,
										{
											color: 'colors.white',
											margin: 0,
											listStyleType: 'disc',
											padding: '0 20px',
											alignItems: 'flex-start',
										},
									]}
								>
									<Box component="li">
										<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
											<strong>Non-Commercial Plan:</strong> Content generated under the
											Non-Commercial Plan may be used for our internal model training, marketing
											materials, demonstrations, and other similar purposes.
										</BaseTypography>
									</Box>
									<Box component="li">
										<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
											<strong>Commercial Plan:</strong> Content generated under the Commercial Plan
											is private to you and will not be used or shared by us except as necessary to
											provide the service to you.
										</BaseTypography>
									</Box>
								</Box>
							</Box>
						</Box>
					</Box>

					<Box sx={[reusableStyles.flexColWithGap(8)]}>
						<BaseTypography variant="t2_bold" styles={{ color: 'colors.white' }}>
							How We Use Your Information
						</BaseTypography>
						<Box
							component="ol"
							sx={[
								reusableStyles.flexColWithGap(12),
								{
									color: 'colors.white',
									margin: 0,
									fontSize: '12px',
									alignItems: 'flex-start',
									fontWeight: 600,
									padding: '0 20px',
								},
							]}
						>
							<Box component="li">
								<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
									<strong>Service Provision:</strong> We use your information to operate, maintain,
									and improve the Platform&apos;s functionality.
								</BaseTypography>
							</Box>
							<Box component="li">
								<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
									<strong>Customer Support:</strong> We use your contact information to respond to
									your inquiries, including any requests to delete your data.
								</BaseTypography>
							</Box>
							<Box component="li">
								<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
									<strong>Marketing and Training (Non-Commercial Plan Only):</strong> For content
									generated under the Non-Commercial Plan, we may use the data for internal training
									of our models, as well as for promotional and marketing materials.
								</BaseTypography>
							</Box>
							<Box component="li">
								<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
									<strong>Analytics:</strong> We may use third-party analytics tools to help us
									measure traffic and usage trends for the Platform.
								</BaseTypography>
							</Box>
						</Box>
					</Box>

					<Box sx={[reusableStyles.flexColWithGap(8)]}>
						<BaseTypography variant="t2_bold" styles={{ color: 'colors.white' }}>
							Sharing of Information
						</BaseTypography>
						<Box
							component="ol"
							sx={[
								reusableStyles.flexColWithGap(12),
								{
									color: 'colors.white',
									margin: 0,
									fontSize: '12px',
									fontWeight: 600,
									alignItems: 'flex-start',
									padding: '0 20px',
								},
							]}
						>
							<Box component="li">
								<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
									<strong>Third-Party Service Providers:</strong> We may share your information with
									third-party vendors who assist us with analytics, storage, and computational
									services. These service providers will only have access to your information as
									needed to perform specific tasks on our behalf and are obligated not to disclose
									or use it for any other purpose.
								</BaseTypography>
							</Box>
							<Box component="li">
								<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
									<strong>Legal Requirements:</strong> We may disclose your information if required
									to do so by law or in response to valid requests by public authorities.
								</BaseTypography>
							</Box>
						</Box>
					</Box>

					<Box sx={[reusableStyles.flexColWithGap(8)]}>
						<BaseTypography variant="t2_bold" styles={{ color: 'colors.white' }}>
							Data Retention and Security
						</BaseTypography>
						<Box
							component="ol"
							sx={[
								reusableStyles.flexColWithGap(12),
								{
									color: 'colors.white',
									margin: 0,
									alignItems: 'flex-start',
									fontSize: '12px',
									fontWeight: 600,
									padding: '0 20px',
								},
							]}
						>
							<Box component="li">
								<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
									<strong>Retention Period:</strong> We retain your personal data and user-generated
									content as long as your account is active or as needed to provide the services. If
									you wish to delete your personal data, you may request deletion within the
									Platform or by contacting us.
								</BaseTypography>
							</Box>
							<Box component="li">
								<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
									<strong>Security Measures:</strong> We implement commercially reasonable
									safeguards to protect your information. However, no method of transmission over
									the internet or electronic storage is 100% secure, and we cannot guarantee
									absolute security.
								</BaseTypography>
							</Box>
						</Box>
					</Box>

					<Box sx={[reusableStyles.flexColWithGap(8)]}>
						<BaseTypography variant="t2_bold" styles={{ color: 'colors.white' }}>
							Your Choices and Rights
						</BaseTypography>
						<Box
							component="ol"
							sx={[
								reusableStyles.flexColWithGap(12),
								{
									color: 'colors.white',
									margin: 0,
									alignItems: 'flex-start',
									fontSize: '12px',
									fontWeight: 600,
									padding: '0 20px',
								},
							]}
						>
							<Box component="li">
								<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
									<strong>Account Information:</strong> You can update, correct, or delete your
									account information at any time by logging into your account or contacting us.
								</BaseTypography>
							</Box>
							<Box component="li">
								<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
									<strong>Request Deletion:</strong> You may request the deletion of your personal
									data and user-generated content (subject to Section 4&apos;s usage rules for
									Non-Commercial Plan data). Upon receiving a valid request, we will delete your
									data unless we are legally required to retain it.
								</BaseTypography>
							</Box>
						</Box>
					</Box>

					<Box sx={[reusableStyles.flexColWithGap(8)]}>
						<BaseTypography variant="t2_bold" styles={{ color: 'colors.white' }}>
							Children&apos;s Privacy
						</BaseTypography>
						<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
							Our Service is not intended for individuals under 18. We do not knowingly collect
							personal information from anyone under 18. If you believe we have inadvertently
							collected such information, please contact us to request deletion.
						</BaseTypography>
					</Box>

					<Box sx={[reusableStyles.flexColWithGap(8)]}>
						<BaseTypography variant="t2_bold" styles={{ color: 'colors.white' }}>
							Changes to This Privacy Policy
						</BaseTypography>
						<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
							We may update this Privacy Policy from time to time. Any changes will be effective
							upon posting of the revised Policy. Your continued use of the Service after the
							posting of changes constitutes your acceptance of such changes.
						</BaseTypography>
					</Box>

					<Box sx={[reusableStyles.flexColWithGap(8)]}>
						<BaseTypography variant="t2_bold" styles={{ color: 'colors.white' }}>
							Contact Us
						</BaseTypography>
						<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
							If you have any questions or concerns regarding this Privacy Policy, please contact us
							at:
						</BaseTypography>
						<Box
							component="ul"
							sx={[
								reusableStyles.flexCol,
								{
									color: 'colors.white',
									margin: 0,
									listStyleType: 'disc',
									padding: '0 20px',
									alignItems: 'flex-start',
								},
							]}
						>
							<Box component="li">
								<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
									Walrus Tech Inc.
								</BaseTypography>
							</Box>
							<Box component="li">
								<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
									66 Franklin St, Suite 300, Oakland, CA 94607
								</BaseTypography>
							</Box>
							<Box component="li">
								<a
									href="mailto:hello@affogato.ai"
									style={{ textDecoration: 'underline', color: 'inherit' }}
								>
									<BaseTypography variant="b2_regular" styles={{ color: 'colors.white' }}>
										hello@affogato.ai
									</BaseTypography>
								</a>
							</Box>
						</Box>
					</Box>
				</Box>
			</Box>
		</Container>
	);
}
